import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import ColorInput from './color-input';
import isValidHex from '../helpers/is-valid-hex';
import constants from '../constants/color-types';
import Tooltip from '../../tooltip';
import SimpleSelect from '../../simple-select';
import HorizontalStack from '../../horizontal-stack';

const { HEX_TYPE, RGB_TYPE, HSL_TYPE } = constants;
const TOOLTIP_ID = `color_picker_alpha_${uuid()}`;
const options = [
  { label: 'HEX', value: HEX_TYPE },
  { label: 'RGB', value: RGB_TYPE },
  { label: 'HSL', value: HSL_TYPE },
];

const defaultColorType = options[1];

const InputFields = ({ hex, onChange, rgb, hsl, enableAlpha, alphaTooltipLabel = '' }) => {
  const [colorType, setColorType] = useState(defaultColorType);

  const isTooltipEnabled = !!alphaTooltipLabel;
  const tooltipAttrsForAlpha = {
    'data-tip': true,
    'data-for': TOOLTIP_ID,
  };

  const handleRGBChange = (data) => {
    const dataCopy = { ...data };

    if (dataCopy.a) {
      dataCopy.a = parseInt(data.a, 10) / 100;
    }

    onChange({ ...rgb, ...dataCopy });
  };

  const handleHSLChange = (data) => {
    const dataCopy = { ...data };

    if (dataCopy.s) {
      dataCopy.s = parseInt(data.s, 10) / 100;
    }
    if (dataCopy.l) {
      dataCopy.l = parseInt(data.l, 10) / 100;
    }

    onChange({ ...hsl, ...dataCopy });
  };

  const handleHEXChange = (data) => {
    if (isValidHex(data.hex)) {
      onChange(data.hex);
    }
  };

  return (
    <div className="ws-ui-color-picker__input-fields" translate="no">
      <div className="ws-ui-color-picker__color-type">
        <SimpleSelect transparent value={colorType} options={options} onChange={setColorType} />
      </div>

      <HorizontalStack>
        {colorType.value === HEX_TYPE && (
          <div className="ws-ui-color-picker__hex-input">
            <ColorInput align="left" name={HEX_TYPE} value={hex} onChange={handleHEXChange} />
          </div>
        )}
        {colorType.value === RGB_TYPE && (
          <React.Fragment>
            <ColorInput name="r" min="0" max="255" value={rgb.r} onChange={handleRGBChange} />
            <ColorInput name="g" min="0" max="255" value={rgb.g} onChange={handleRGBChange} />
            <ColorInput name="b" min="0" max="255" value={rgb.b} onChange={handleRGBChange} />
          </React.Fragment>
        )}
        {colorType.value === HSL_TYPE && (
          <React.Fragment>
            <ColorInput
              name="h"
              min="0"
              max="360"
              value={Math.round(hsl.h)}
              onChange={handleHSLChange}
            />
            <ColorInput
              name="s"
              min="0"
              max="100"
              value={Math.round(hsl.s * 100)}
              onChange={handleHSLChange}
            />
            <ColorInput
              name="l"
              min="0"
              max="100"
              value={Math.round(hsl.l * 100)}
              onChange={handleHSLChange}
            />
          </React.Fragment>
        )}
        {enableAlpha && (
          <div
            className="ws-ui-color-picker__alpha-input"
            {...(isTooltipEnabled && tooltipAttrsForAlpha)}
          >
            <ColorInput
              isValuePercent
              name="a"
              min="0"
              max="100"
              value={`${Math.round(rgb.a * 100)}%`}
              onChange={handleRGBChange}
            />
            {isTooltipEnabled && <Tooltip text={alphaTooltipLabel} id={TOOLTIP_ID} />}
          </div>
        )}
      </HorizontalStack>
    </div>
  );
};

InputFields.propTypes = {
  hex: PropTypes.string.isRequired,
  rgb: PropTypes.shape({
    r: PropTypes.number.isRequired,
    g: PropTypes.number.isRequired,
    b: PropTypes.number.isRequired,
    a: PropTypes.number.isRequired,
  }).isRequired,
  hsl: PropTypes.shape({
    h: PropTypes.number.isRequired,
    s: PropTypes.number.isRequired,
    l: PropTypes.number.isRequired,
    a: PropTypes.number.isRequired,
  }).isRequired,
  enableAlpha: PropTypes.bool.isRequired,
  alphaTooltipLabel: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default InputFields;
